/**
 * Materials API — typed migration of MaterialsAPI.js
 */
import apiClient from './client';
import { getAuthToken } from './client';
import type { Material, MaterialCollection, ApiResult } from './types';

// Base URL for fetch() calls — must match apiClient baseURL:
// DEV: '/api' (goes through Vite proxy which strips /api before forwarding)
// PROD: VITE_API_ENDPOINT directly (no /api prefix)
const getBaseUrl = () => import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_ENDPOINT as string);

const MaterialsAPI = {
  /**
   * Get materials with optional server-side filtering and pagination.
   * @param opts.type   'video' | 'image' | 'audio' — server filters by type OR URL extension
   * @param opts.limit  How many items per page (default: all)
   * @param opts.skip   How many items to skip (for pagination)
   * @param opts.creatorId  Filter by creator
   * Returns { items, total }
   */
  async getUserMaterials(opts: {
    type?: string;
    limit?: number;
    skip?: number;
    creatorId?: string;
    excludeCreatorId?: string;  // «Все» tab: exclude own materials
    access?: string;            // «Все» tab: 'public' — others' public only
    search?: string;
  } = {}): Promise<{ items: Material[]; total: number }> {
    const token = getAuthToken();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90_000);

    const params = new URLSearchParams();
    if (opts.type)              params.set('type', opts.type);
    if (opts.limit)             params.set('limit', String(opts.limit));
    if (opts.skip)              params.set('skip', String(opts.skip));
    if (opts.creatorId)         params.set('creatorId', opts.creatorId);
    if (opts.excludeCreatorId)  params.set('excludeCreatorId', opts.excludeCreatorId);
    if (opts.access)            params.set('access', opts.access);

    const hasSearch = opts.search && opts.search.trim();

    // When search is provided, use /v3/materials/search which performs
    // a real MongoDB regex search across all 53k+ videos.
    // Without search, use /v2/materials for backward compatibility.
    if (hasSearch) {
      params.set('q', opts.search!.trim());
    } else if (opts.search && opts.search.trim()) {
      params.set('search', opts.search.trim());
    }

    const endpoint = hasSearch ? '/v3/materials/search' : '/v2/materials';
    const qs = params.toString() ? `?${params.toString()}` : '';

    try {
      const res = await fetch(`${getBaseUrl()}${endpoint}${qs}`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token } : {}),
        },
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as ApiResult<Material[]> & { total?: number };
      if (data.error) throw new Error((data.error as any).message);
      return { items: data.result ?? [], total: data.total ?? (data.result?.length ?? 0) };
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  },


  /** Create a new material */
  async createMaterial(payload: Partial<Material>): Promise<Material> {
    const { data } = await apiClient.post<Material>('/v2/materials', payload);
    return data;
  },

  /** Update an existing material */
  async updateMaterial(payload: Partial<Material> & { id: string }): Promise<Material> {
    const { data } = await apiClient.put<Material>('/v2/materials', payload);
    return data;
  },



  /** Delete a material */
  async deleteMaterial(id: string): Promise<void> {
    await apiClient.delete(`/v3/materials/${id}`);
  },

  /** Get materials inside a specific collection (server does the ID lookup) */
  async getCollectionMaterials(collectionId: string): Promise<Material[]> {
    const { data } = await apiClient.get<any>(`/smart-folders/${collectionId}/exercises`);
    const items = Array.isArray(data) ? data : (data?.result || []);
    return items;
  },

  /* ── Collections (SmartFolders) ── */

  /** Get collections (smart folders) for current user */
  async getUserCollections(): Promise<MaterialCollection[]> {
    const { data } = await apiClient.get<any>('/smart-folders');
    // Smart folders API returns array directly
    const folders = Array.isArray(data) ? data : (data.result || []);
    return folders;
  },

  /** Create collection */
  async createCollection(payload: any): Promise<MaterialCollection> {
    const { data } = await apiClient.post<any>('/smart-folders', payload);
    return data;
  },

  /** Update collection */
  async updateCollection(payload: any): Promise<MaterialCollection> {
    const { data } = await apiClient.put<any>('/smart-folders', payload);
    return data;
  },

  /** Delete collection */
  async deleteCollection(id: string): Promise<void> {
    await apiClient.delete(`/smart-folders/${id}`);
  },

  /** Get idioms materials (special collection) */
  async getIdiomsMaterials(): Promise<Material[]> {
    const { data } = await apiClient.get<any>('/v3/idioms-materials');
    // Handle both direct array and { result: [...] } response formats
    if (Array.isArray(data)) return data;
    if (data?.result && Array.isArray(data.result)) return data.result;
    if (data?.items && Array.isArray(data.items)) return data.items;
    return data ?? [];
  },

  /** Get dictionary materials (special collection) */
  async getDictionaryMaterials(): Promise<Material[]> {
    const { data } = await apiClient.get<any>('/v3/dictionary-materials');
    // Handle both direct array and { result: [...] } response formats
    if (Array.isArray(data)) return data;
    if (data?.result && Array.isArray(data.result)) return data.result;
    if (data?.items && Array.isArray(data.items)) return data.items;
    return data ?? [];
  },
};

export default MaterialsAPI;
